import { useState, useEffect, useRef } from 'react';
import { Plus, RefreshCw, X, ChevronDown, ExternalLink, Briefcase } from 'lucide-react';
import Header from '../components/layout/Header';
import {
  getJobApplications,
  getJobSearchStats,
  createJobApplication,
  updateJobApplication,
  triggerJobDiscovery,
} from '../services/api';
import type { JobApplication, JobApplicationStatus, JobApplicationPriority, JobSearchStats } from '../types';

// ── Pipeline column definitions ──────────────────────────────────────

const PIPELINE_COLUMNS: { key: JobApplicationStatus; label: string; color: string }[] = [
  { key: 'research', label: 'Research', color: 'var(--text-muted)' },
  { key: 'drafted', label: 'Drafted', color: 'var(--info)' },
  { key: 'applied', label: 'Applied', color: 'var(--warning)' },
  { key: 'phone_screen', label: 'Phone Screen', color: '#a78bfa' },
  { key: 'technical_interview', label: 'Technical', color: '#60a5fa' },
  { key: 'final_interview', label: 'Final Interview', color: '#f472b6' },
  { key: 'offer', label: 'Offer', color: 'var(--success)' },
];

const PRIORITY_COLORS: Record<JobApplicationPriority, { bg: string; text: string; label: string }> = {
  HIGH: { bg: 'rgba(239,68,68,0.15)', text: '#ef4444', label: 'HIGH' },
  MEDIUM: { bg: 'rgba(234,179,8,0.15)', text: '#eab308', label: 'MED' },
  LOW: { bg: 'rgba(144,185,171,0.15)', text: 'var(--netrun-green)', label: 'LOW' },
};

// ── Utilities ────────────────────────────────────────────────────────

function daysSince(dateStr: string | null | undefined): number {
  if (!dateStr) return 0;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function formatDays(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return '1d ago';
  return `${days}d ago`;
}

// ── Application Card ─────────────────────────────────────────────────

interface CardProps {
  app: JobApplication;
  onDragStart: (e: React.DragEvent, app: JobApplication) => void;
  onClick: (app: JobApplication) => void;
}

function ApplicationCard({ app, onDragStart, onClick }: CardProps) {
  const priority = PRIORITY_COLORS[app.priority] ?? PRIORITY_COLORS.MEDIUM;
  const days = daysSince(app.updated_at);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, app)}
      onClick={() => onClick(app)}
      className="rounded-lg p-3 cursor-grab active:cursor-grabbing transition-all duration-200 select-none"
      style={{
        background: 'var(--bg-input)',
        border: '1px solid var(--border-primary)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(144,185,171,0.35)')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
    >
      {/* Company + priority badge */}
      <div className="flex items-start justify-between gap-2 mb-1">
        <p className="text-sm font-body leading-snug" style={{ color: 'var(--text-primary)' }}>
          {app.company_name}
        </p>
        <span
          className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
          style={{ background: priority.bg, color: priority.text }}
        >
          {priority.label}
        </span>
      </div>

      {/* Role */}
      <p className="text-xs mb-2 leading-snug" style={{ color: 'var(--text-secondary)' }}>
        {app.role_title}
      </p>

      {/* Footer row */}
      <div className="flex items-center justify-between">
        {app.notes ? (
          <p className="text-[10px] truncate max-w-[140px]" style={{ color: 'var(--text-muted)' }}>
            {app.notes}
          </p>
        ) : (
          <span />
        )}
        <span className="text-[10px] flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
          {formatDays(days)}
        </span>
      </div>
    </div>
  );
}

// ── Kanban Column ────────────────────────────────────────────────────

interface ColumnProps {
  status: JobApplicationStatus;
  label: string;
  color: string;
  apps: JobApplication[];
  onDragStart: (e: React.DragEvent, app: JobApplication) => void;
  onDrop: (e: React.DragEvent, status: JobApplicationStatus) => void;
  onCardClick: (app: JobApplication) => void;
}

function KanbanColumn({ status, label, color, apps, onDragStart, onDrop, onCardClick }: ColumnProps) {
  const [isOver, setIsOver] = useState(false);

  return (
    <div className="flex-shrink-0 w-[260px] snap-center md:snap-align-none">
      {/* Column header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
          <span className="text-sm font-body" style={{ color: 'var(--text-primary)' }}>
            {label}
          </span>
          <span
            className="text-xs px-1.5 py-0.5 rounded-full"
            style={{ background: 'var(--bg-input)', color: 'var(--text-muted)' }}
          >
            {apps.length}
          </span>
        </div>
      </div>

      {/* Drop zone */}
      <div
        className="space-y-2 min-h-[160px] rounded-xl p-2.5 transition-all duration-150"
        style={{
          background: isOver ? 'rgba(144,185,171,0.06)' : 'rgba(17,17,17,0.5)',
          border: `1px solid ${isOver ? 'rgba(144,185,171,0.4)' : 'var(--border-primary)'}`,
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsOver(true);
        }}
        onDragLeave={() => setIsOver(false)}
        onDrop={(e) => {
          setIsOver(false);
          onDrop(e, status);
        }}
      >
        {apps.map((app) => (
          <ApplicationCard
            key={app.id}
            app={app}
            onDragStart={onDragStart}
            onClick={onCardClick}
          />
        ))}
        {apps.length === 0 && (
          <p className="text-xs text-center py-10" style={{ color: 'var(--text-muted)' }}>
            Drop here
          </p>
        )}
      </div>
    </div>
  );
}

// ── Stats Bar ────────────────────────────────────────────────────────

interface StatsBarProps {
  stats: JobSearchStats | null;
  totalApps: number;
  onRunDiscovery: () => void;
  discovering: boolean;
}

function StatsBar({ stats, totalApps, onRunDiscovery, discovering }: StatsBarProps) {
  const active = stats
    ? Object.entries(stats.by_status)
        .filter(([s]) => !['accepted', 'rejected', 'withdrawn'].includes(s))
        .reduce((acc, [, v]) => acc + v, 0)
    : 0;

  const applied = stats?.by_status?.applied ?? 0;
  const responses = stats
    ? Object.entries(stats.by_status)
        .filter(([s]) => ['phone_screen', 'technical_interview', 'final_interview', 'offer', 'accepted'].includes(s))
        .reduce((acc, [, v]) => acc + v, 0)
    : 0;
  const responseRate = applied > 0 ? Math.round((responses / applied) * 100) : 0;

  const statItems = [
    { label: 'Total Applications', value: totalApps },
    { label: 'Active Pipeline', value: active },
    { label: 'Response Rate', value: `${responseRate}%` },
    { label: 'Applied', value: applied },
  ];

  return (
    <div
      className="flex items-center gap-4 px-4 md:px-8 py-3 overflow-x-auto"
      style={{ borderBottom: '1px solid var(--border-primary)', background: 'var(--bg-header)' }}
    >
      <div className="flex items-center gap-6 flex-1">
        {statItems.map((s) => (
          <div key={s.label} className="flex-shrink-0">
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {s.label}
            </p>
            <p className="text-lg font-display" style={{ color: 'var(--netrun-green)' }}>
              {s.value}
            </p>
          </div>
        ))}
      </div>

      <button
        onClick={onRunDiscovery}
        disabled={discovering}
        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-body flex-shrink-0 transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        style={{
          background: 'var(--netrun-green)',
          color: 'var(--netrun-black)',
          border: 'none',
        }}
      >
        <RefreshCw size={14} className={discovering ? 'animate-spin' : ''} />
        {discovering ? 'Running...' : 'Run Discovery'}
      </button>
    </div>
  );
}

// ── Add Application Modal ─────────────────────────────────────────────

interface AddModalProps {
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => Promise<void>;
}

function AddApplicationModal({ onClose, onSave }: AddModalProps) {
  const [form, setForm] = useState({
    company_name: '',
    role_title: '',
    career_page_url: '',
    job_url: '',
    priority: 'MEDIUM' as JobApplicationPriority,
    notes: '',
    status: 'research' as JobApplicationStatus,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.role_title.trim()) {
      setError('Company and role are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {
        company_name: form.company_name.trim(),
        role_title: form.role_title.trim(),
        priority: form.priority,
        status: form.status,
      };
      if (form.career_page_url.trim()) payload.career_page_url = form.career_page_url.trim();
      if (form.job_url.trim()) payload.job_url = form.job_url.trim();
      if (form.notes.trim()) payload.notes = form.notes.trim();
      await onSave(payload);
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save application.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-md rounded-xl p-6 slide-up"
        style={{ background: '#141414', border: '1px solid var(--border-primary)' }}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-lg" style={{ color: 'var(--text-primary)' }}>
            Add Application
          </h3>
          <button onClick={onClose} style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Company */}
          <div>
            <label className="block text-xs mb-1.5" style={{ color: 'var(--text-muted)' }}>
              Company *
            </label>
            <input
              type="text"
              required
              value={form.company_name}
              onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
              placeholder="Acme Corp"
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-primary)',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
            />
          </div>

          {/* Role */}
          <div>
            <label className="block text-xs mb-1.5" style={{ color: 'var(--text-muted)' }}>
              Role Title *
            </label>
            <input
              type="text"
              required
              value={form.role_title}
              onChange={(e) => setForm((f) => ({ ...f, role_title: e.target.value }))}
              placeholder="Senior Cloud Engineer"
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-primary)',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
            />
          </div>

          {/* Career Page URL */}
          <div>
            <label className="block text-xs mb-1.5" style={{ color: 'var(--text-muted)' }}>
              Career Page URL
            </label>
            <input
              type="url"
              value={form.career_page_url}
              onChange={(e) => setForm((f) => ({ ...f, career_page_url: e.target.value }))}
              placeholder="https://company.com/careers"
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-primary)',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
            />
          </div>

          {/* Job Posting URL */}
          <div>
            <label className="block text-xs mb-1.5" style={{ color: 'var(--text-muted)' }}>
              Job Posting URL
            </label>
            <input
              type="url"
              value={form.job_url}
              onChange={(e) => setForm((f) => ({ ...f, job_url: e.target.value }))}
              placeholder="https://company.com/jobs/123"
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-primary)',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
            />
          </div>

          {/* Priority + Status row */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs mb-1.5" style={{ color: 'var(--text-muted)' }}>
                Priority
              </label>
              <div className="relative">
                <select
                  value={form.priority}
                  onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value as JobApplicationPriority }))}
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none appearance-none"
                  style={{
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border-primary)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
              </div>
            </div>

            <div className="flex-1">
              <label className="block text-xs mb-1.5" style={{ color: 'var(--text-muted)' }}>
                Stage
              </label>
              <div className="relative">
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as JobApplicationStatus }))}
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none appearance-none"
                  style={{
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border-primary)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {PIPELINE_COLUMNS.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs mb-1.5" style={{ color: 'var(--text-muted)' }}>
              Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              placeholder="Referred by..., key requirements, etc."
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none resize-none"
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-primary)',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
            />
          </div>

          {error && (
            <p className="text-xs" style={{ color: '#ef4444' }}>
              {error}
            </p>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-body cursor-pointer"
              style={{
                background: 'var(--bg-input)',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border-primary)',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 rounded-lg text-sm font-body cursor-pointer disabled:opacity-60"
              style={{
                background: 'var(--netrun-green)',
                color: 'var(--netrun-black)',
                border: 'none',
              }}
            >
              {saving ? 'Saving...' : 'Add Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Detail Drawer ────────────────────────────────────────────────────

interface DetailDrawerProps {
  app: JobApplication;
  onClose: () => void;
  onStatusChange: (id: string, status: JobApplicationStatus) => Promise<void>;
}

function DetailDrawer({ app, onClose, onStatusChange }: DetailDrawerProps) {
  const priority = PRIORITY_COLORS[app.priority] ?? PRIORITY_COLORS.MEDIUM;
  const [changing, setChanging] = useState(false);

  const handleStatusChange = async (newStatus: JobApplicationStatus) => {
    setChanging(true);
    try {
      await onStatusChange(app.id, newStatus);
    } finally {
      setChanging(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full md:max-w-lg rounded-t-2xl md:rounded-xl overflow-y-auto slide-up"
        style={{
          background: '#141414',
          border: '1px solid var(--border-primary)',
          maxHeight: '85vh',
        }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between p-5"
          style={{ borderBottom: '1px solid var(--border-primary)' }}
        >
          <div className="min-w-0 pr-3">
            <h3 className="font-display text-base" style={{ color: 'var(--text-primary)' }}>
              {app.company_name}
            </h3>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
              {app.role_title}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className="text-xs font-bold px-2 py-1 rounded"
              style={{ background: priority.bg, color: priority.text }}
            >
              {priority.label}
            </span>
            <button
              onClick={onClose}
              style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          {/* Move stage */}
          <div>
            <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
              Pipeline Stage
            </p>
            <div className="flex flex-wrap gap-2">
              {PIPELINE_COLUMNS.map((col) => (
                <button
                  key={col.key}
                  onClick={() => handleStatusChange(col.key)}
                  disabled={changing || app.status === col.key}
                  className="text-xs px-3 py-1.5 rounded-lg transition-all duration-150 cursor-pointer disabled:cursor-default"
                  style={{
                    background:
                      app.status === col.key ? col.color : 'var(--bg-input)',
                    color:
                      app.status === col.key
                        ? app.status === 'research'
                          ? 'var(--text-primary)'
                          : '#000'
                        : 'var(--text-secondary)',
                    border: `1px solid ${app.status === col.key ? col.color : 'var(--border-primary)'}`,
                    opacity: changing && app.status !== col.key ? 0.5 : 1,
                  }}
                >
                  {col.label}
                </button>
              ))}
            </div>
          </div>

          {/* Links */}
          {(app.job_url || app.career_page_url) && (
            <div>
              <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
                Links
              </p>
              <div className="flex flex-wrap gap-2">
                {app.job_url && (
                  <a
                    href={app.job_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                    style={{ color: 'var(--netrun-green)', background: 'rgba(144,185,171,0.1)', border: '1px solid rgba(144,185,171,0.2)' }}
                  >
                    <ExternalLink size={12} />
                    Job Posting
                  </a>
                )}
                {app.career_page_url && (
                  <a
                    href={app.career_page_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                    style={{ color: 'var(--netrun-green)', background: 'rgba(144,185,171,0.1)', border: '1px solid rgba(144,185,171,0.2)' }}
                  >
                    <ExternalLink size={12} />
                    Careers Page
                  </a>
                )}
              </div>
            </div>
          )}

          {/* ATS platform */}
          {app.ats_platform && (
            <div>
              <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>ATS Platform</p>
              <p className="text-sm capitalize" style={{ color: 'var(--text-primary)' }}>{app.ats_platform}</p>
            </div>
          )}

          {/* Next action */}
          {app.next_action && (
            <div>
              <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Next Action</p>
              <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{app.next_action}</p>
              {app.next_action_date && (
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  Due: {new Date(app.next_action_date).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          {/* Notes */}
          {app.notes && (
            <div>
              <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Notes</p>
              <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>
                {app.notes}
              </p>
            </div>
          )}

          {/* AI intel */}
          {app.role_analysis && (
            <div
              className="rounded-lg p-3"
              style={{ background: 'rgba(144,185,171,0.06)', border: '1px solid rgba(144,185,171,0.15)' }}
            >
              <p className="text-xs font-bold mb-1" style={{ color: 'var(--netrun-green)' }}>
                AI Role Analysis
              </p>
              <pre className="text-xs whitespace-pre-wrap" style={{ color: 'var(--text-secondary)' }}>
                {typeof app.role_analysis === 'string'
                  ? app.role_analysis
                  : JSON.stringify(app.role_analysis, null, 2)}
              </pre>
            </div>
          )}

          {/* Timestamps */}
          <div className="flex gap-6 pt-1">
            <div>
              <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Created</p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                {new Date(app.created_at).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Last Updated</p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                {new Date(app.updated_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────

export default function JobSearchPage() {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [stats, setStats] = useState<JobSearchStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<JobApplicationPriority | 'all'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState<JobApplication | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const dragRef = useRef<JobApplication | null>(null);

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const [appsData, statsData] = await Promise.all([
        getJobApplications(),
        getJobSearchStats(),
      ]);
      const items: JobApplication[] = appsData?.items ?? appsData ?? [];
      setApplications(Array.isArray(items) ? items : []);
      setStats(statsData ?? null);
    } catch (err) {
      console.error('Failed to load job search data:', err);
      setError('Failed to load applications. Check API connection.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  // Filter applications for display
  const visibleApps = applications.filter((app) => {
    if (priorityFilter !== 'all' && app.priority !== priorityFilter) return false;
    return true;
  });

  // Build columns only from pipeline statuses (not accepted/rejected/withdrawn)
  const boardColumns = PIPELINE_COLUMNS.map((col) => ({
    ...col,
    apps: visibleApps.filter((a) => a.status === col.key),
  }));

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, app: JobApplication) => {
    dragRef.current = app;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (e: React.DragEvent, newStatus: JobApplicationStatus) => {
    e.preventDefault();
    const app = dragRef.current;
    dragRef.current = null;
    if (!app || app.status === newStatus) return;

    // Optimistic update
    setApplications((prev) =>
      prev.map((a) => (a.id === app.id ? { ...a, status: newStatus } : a))
    );

    try {
      await updateJobApplication(app.id, { status: newStatus });
    } catch (err) {
      console.error('Failed to update status:', err);
      // Revert
      setApplications((prev) =>
        prev.map((a) => (a.id === app.id ? { ...a, status: app.status } : a))
      );
    }
  };

  const handleStatusChange = async (id: string, newStatus: JobApplicationStatus) => {
    setApplications((prev) =>
      prev.map((a) => (a.id === id ? { ...a, status: newStatus } : a))
    );
    if (selectedApp && selectedApp.id === id) {
      setSelectedApp((prev) => prev ? { ...prev, status: newStatus } : prev);
    }
    try {
      await updateJobApplication(id, { status: newStatus });
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleAdd = async (data: Record<string, unknown>) => {
    const newApp = await createJobApplication(data);
    setApplications((prev) => [newApp, ...prev]);
  };

  const handleRunDiscovery = async () => {
    setDiscovering(true);
    try {
      await triggerJobDiscovery();
    } catch (err) {
      console.error('Discovery failed:', err);
    } finally {
      setDiscovering(false);
    }
  };

  // Subtitle counts
  const activeCount = applications.filter(
    (a) => !['accepted', 'rejected', 'withdrawn'].includes(a.status)
  ).length;

  if (loading) {
    return (
      <div className="page-enter">
        <Header title="Job Search" subtitle="Loading pipeline..." />
        <div className="p-4 md:p-8 text-center" style={{ color: 'var(--text-muted)' }}>
          <Briefcase size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Loading job search pipeline...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <Header
        title="Job Search"
        subtitle={`${applications.length} applications -- ${activeCount} active`}
      />

      {/* Stats Bar */}
      <StatsBar
        stats={stats}
        totalApps={applications.length}
        onRunDiscovery={handleRunDiscovery}
        discovering={discovering}
      />

      {/* Filter Bar */}
      <div
        className="flex items-center gap-3 px-4 md:px-8 py-3 overflow-x-auto"
        style={{ borderBottom: '1px solid var(--border-primary)' }}
      >
        {/* Priority filter */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {(['all', 'HIGH', 'MEDIUM', 'LOW'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPriorityFilter(p)}
              className="text-xs px-3 py-1.5 rounded-lg transition-all duration-150 cursor-pointer"
              style={{
                background: priorityFilter === p ? 'var(--netrun-green)' : 'var(--bg-input)',
                color: priorityFilter === p ? 'var(--netrun-black)' : 'var(--text-secondary)',
                border: `1px solid ${priorityFilter === p ? 'var(--netrun-green)' : 'var(--border-primary)'}`,
                fontWeight: priorityFilter === p ? 600 : 400,
              }}
            >
              {p === 'all' ? 'All Priorities' : p}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Add button */}
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-body flex-shrink-0 cursor-pointer"
          style={{
            background: 'var(--bg-input)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-primary)',
          }}
        >
          <Plus size={14} />
          Add Application
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 md:mx-8 mt-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}>
          {error}
        </div>
      )}

      {/* Kanban board */}
      <div className="p-4 md:p-8">
        {applications.length === 0 && !error ? (
          <div className="flex flex-col items-center justify-center py-20" style={{ color: 'var(--text-muted)' }}>
            <Briefcase size={48} className="mb-4 opacity-20" />
            <p className="text-base mb-1" style={{ color: 'var(--text-secondary)' }}>
              No applications yet
            </p>
            <p className="text-sm mb-4">Start tracking your job search pipeline</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-body cursor-pointer"
              style={{ background: 'var(--netrun-green)', color: 'var(--netrun-black)', border: 'none' }}
            >
              <Plus size={14} />
              Add First Application
            </button>
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory md:snap-none pb-4">
            {boardColumns.map((col) => (
              <KanbanColumn
                key={col.key}
                status={col.key}
                label={col.label}
                color={col.color}
                apps={col.apps}
                onDragStart={handleDragStart}
                onDrop={handleDrop}
                onCardClick={setSelectedApp}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddApplicationModal onClose={() => setShowAddModal(false)} onSave={handleAdd} />
      )}
      {selectedApp && (
        <DetailDrawer
          app={selectedApp}
          onClose={() => setSelectedApp(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}
